/*
 * MathGraph32 Javascript : Software for animating online dynamic mathematics figures
 * http://www.mathgraph32.org/
 * @Author Yves Biton (yves.biton@sesamath.net)
 * @License: GNU AGPLv3 https://www.gnu.org/licenses/agpl-3.0.html
 */
const Complexe = require('../types/Complexe')
const Fonte = require('../types/Fonte')
const Ope = require('../types/Ope')
const kernel = require('../kernel/kernel')
const CCb = require('./CCb')
const CCbGlob = require('../kernel/CCbGlob')
module.exports = COperation

/**
 * @constructor
 * @extends CCb
 * Classe représentant une opération dans un arbre binaire de calcul.
 * @param {CListeObjets} listeProprietaire : La liste propriétaire
 * @param {CCb} operande1 : L'opérande de gauche
 * @param {CCb} operande2 : L'opérande de droite
 * @param {Integer} Ope : Caractérise l'opérateur. Voir mtg32.Operateur.
 * @returns {COperation}
 */
function COperation (listeProprietaire, operande1, operande2, Ope) {
  CCb.call(this, listeProprietaire);
  if (arguments.length === 1) CCb.call(this, listeProprietaire);
  else {
    this.operande1 = operande1;
    this.operande2 = operande2;
    this.Ope = Ope;
  }
  this.c1 = new Complexe();
  this.c2 = new Complexe();
}
COperation.prototype = new CCb();
COperation.prototype.constructor = COperation;
COperation.prototype.superClass = "CCb";
COperation.prototype.className = "COperation";

COperation.zeroComp = "0+0*i";


COperation.prototype.nature = function() {
  return CCbGlob.natOperation;
};

COperation.prototype.getClone = function(listeSource, listeCible) {
  var cloneOperande1 = this.operande1.getClone(listeSource, listeCible);
  var cloneOperande2 = this.operande2.getClone(listeSource, listeCible);
  return new COperation(listeCible, cloneOperande1, cloneOperande2, this.Ope);
};

COperation.prototype.initialisePourDependance = function() {
  CCb.prototype.initialisePourDependance.call(this);
  this.operande1.initialisePourDependance();
  this.operande2.initialisePourDependance();
};

COperation.prototype.dependDe = function(p) {
  return this.memoriseDependance(this.operande1.dependDe(p) || this.operande2.dependDe(p));
};

COperation.prototype.dependDePourBoucle = function(p) {
  return this.operande1.dependDePourBoucle(p) || this.operande2.dependDePourBoucle(p);
};

COperation.prototype.existe = function(infoRandom) {
  return this.operande1.existe(infoRandom) && this.operande2.existe(infoRandom);
};
/**
 * Fonction renvoyant le résultat de l'opérateur appliqué aux nombres réels
 * op1 (opérande de gauche) et op2 (opérande de droite).
 * @param {Float} op1 : Opérande de gauche
 * @param {Float} op2 : Opérande de droite
 * @returns {Float} : Le résultat de l'opération
 */
COperation.prototype.resultatBase = function(op1, op2){
  var denom;
  switch (this.Ope)
  {
    case Ope.Plus:
      return op1 + op2;
    case Ope.Moin:
      return op1 - op2;
    case Ope.Mult:
      return op1 * op2;
    // à revoir pour gestion des exceptions
    case Ope.Divi:
       denom = op2;
       if (denom === 0) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
       else return op1 / denom;
    case Ope.Inf:
      if (op1 < op2)
              return 1;
      else return 0;
    case Ope.Sup:
      if (op1 > op2)
              return 1;
      else return 0;
    case Ope.InfOuEgal:
      if (op1 <= op2)
              return 1;
      else return 0;
    case Ope.SupOuEgal:
      if (op1 >= op2)
              return 1;
      else return 0;
    case Ope.Egalite:
      if (op1 === op2)
              return 1;
      else return 0;
    case Ope.Diff:
      if (op1 !== op2)
              return 1;
      else return 0;
        // Ajout version 4.9.5
    case Ope.And:
      if ((op1===1) && (op2===1)) return 1; else return 0;
    case Ope.Or:
      if ((op1===1) || (op2===1)) return 1; else return 0;

    default: return 0;
  } // switch Ope
};

COperation.prototype.resultat = function(infoRandom){
   var o1 = this.operande1.resultat(infoRandom);
   var o2 = this.operande2.resultat(infoRandom);
   return this.resultatBase(o1, o2);
};

COperation.prototype.resultatFonction = function(infoRandom, valeurParametre){
   var o1 = this.operande1.resultatFonction(infoRandom, valeurParametre);
   var o2 = this.operande2.resultatFonction(infoRandom, valeurParametre);
   return this.resultatBase(o1, o2);
};
// op1 et op2 pointent sur deux complexes : les opérandes
/**
 * Fonction renvoyant le résultat de l'opérateur appliqué aux nombres complexes
 * pointés par op1 (opérande de gauche) et op2 (opérande de droite).
 * @param {Complexe} op1 : Opérande de gauche
 * @param {Complexe} op2 : Opérande de droite
 * @param {type} zRes : Le complexe dans lequel on met le résultat de l'opération.
 * @returns {undefined}
 */
COperation.prototype.resultatComplexeBase = function(op1, op2, zRes) {
  switch (this.Ope)
  {
    case Ope.Plus:
            zRes.x = op1.x + op2.x;
            zRes.y = op1.y + op2.y;
            return;
     case Ope.Moin:
            zRes.x = op1.x - op2.x;
            zRes.y = op1.y - op2.y;
            return;
    case Ope.Mult:
            zRes.x = op1.x*op2.x - op1.y*op2.y;
            zRes.y = op1.x*op2.y + op1.y*op2.x;
            return;
    case Ope.Divi:
        if ((op2.x === 0) && (op2.y === 0)) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
        else kernel.divComp(op1, op2, zRes);
          return;
    case Ope.Inf:
      // Si on est dans C, on n'accepte de comparer deux complexes
      // que s'ils sont réels
        zRes.x = 0;
        zRes.y = 0;
        if ((op1.y !== 0) || (op2.y !== 0))
          throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
        else
        {
          var mod1 = op1.x*op1.x + op1.y*op1.y;
          var mod2 = op2.x*op2.x + op2.y*op2.y;
          if (mod1 < mod2) zRes.x = 1;
          else zRes.x = 0;
          return;
        }
    case Ope.Sup:
      // Si on est dans C, on n'accepte de comparer deux complexes
      // que s'ils sont réels
        zRes.x = 0;
        zRes.y = 0;
        if ((op1.y !== 0) || (op2.y !== 0))
          throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
        else {
          if (op1.x > op2.x) zRes.x = 1;
          else zRes.x = 0;
          return;
        }
    case Ope.InfOuEgal:
      // Si on est dans C, on n'accepte de comparer deux complexes
      // que s'ils sont réels
        zRes.x = 0;
        zRes.y = 0;
        if ((op1.y !== 0) || (op2.y !== 0))
          throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
        else {
          if (op1.x <= op2.x) zRes.x = 1;
          else zRes.x = 0;
          return;
        }
    case Ope.SupOuEgal:
      // Si on est dans C, on n'accepte de comparer deux complexes
      // que s'ils sont réels
        zRes.x = 0;
        zRes.y = 0;
        if ((op1.y !== 0) || (op2.y !== 0)) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
        else {
          if (op1.x >= op2.x) zRes.x = 1;
          else zRes.x = 0;
          return;
        }
    case Ope.Egalite:
      zRes.y = 0;
      if ((op1.x === op2.x) && (op1.y === op2.y)) zRes.x = 1;
      else zRes.x = 0;
      return;
    case Ope.Diff:
      zRes.y = 0;
      if ((op1.x !== op2.x) || (op1.y !== op2.y)) zRes.x = 1;
      else zRes.x = 0;
      return;
      // Ajout version 4.9.5
      case Ope.And:
      	zRes.y = 0;
      	if ((op1.y !== 0) || (op2.y !== 0)) zRes.x = 0; // aux si les opérandes ne sont pas réels
      	else {
      		if ((op1.x === 1) && (op2.x === 1)) zRes.x = 1;
      		else zRes.x = 0;
      	}
      	return;
      case Ope.Or:
      	zRes.y = 0;
      	if ((op1.y !== 0) || (op2.y !== 0)) zRes.x = 0; // Faux si les opérandes ne sont pas réels
      	else {
      		if ((op1.x === 1) || (op2.x === 1)) zRes.x = 1;
      		else zRes.x = 0;
      	}
      	return;

  } // switch Ope
};

COperation.prototype.resultatComplexe = function(infoRandom, zRes) {
  this.operande1.resultatComplexe(infoRandom, this.c1);
  this.operande2.resultatComplexe(infoRandom, this.c2);
  this.resultatComplexeBase(this.c1, this.c2, zRes);
};

COperation.prototype.resultatFonctionComplexe = function(infoRandom, valeurParametre, zRes){
  this.operande1.resultatFonctionComplexe(infoRandom, valeurParametre, this.c1);
  this.operande2.resultatFonctionComplexe(infoRandom, valeurParametre, this.c2);
  this.resultatComplexeBase(this.c1, this.c2, zRes);
};

COperation.prototype.dependDeVariable = function(ind) {
  return this.operande1.dependDeVariable(ind) || this.operande2.dependDeVariable(ind);
};

COperation.prototype.getCopie = function() {
  return new COperation(this.listeProprietaire, this.operande1.getCopie(), this.operande2.getCopie(),
    this.Ope);
};

COperation.prototype.chaineCalcul = function(varFor) {
  var ch1 ="", ch2 = "", ch = "";
  var parouv = "(";
  var parfer = ")";
  switch (this.Ope)
  {
    case Ope.Plus:
      if (this.operande1.nature() === CCbGlob.natOperation) {
        var op1 = this.operande1.Ope;
        if ((op1 === Ope.Plus) || (op1 === Ope.Moin)
            || (op1 === Ope.Mult) || (op1 === Ope.Divi))
          ch1 = this.operande1.chaineCalculSansPar(varFor);
        else ch1 = this.operande1.chaineCalcul(varFor);
      }
      else {
        // else ch1 = operande1.chaineCalcul(varFor);
        // Rajouté version 4.6
        if (this.operande1.nature() === CCbGlob.natMoinsUnaire) ch1 = this.operande1.chaineCalculSansPar(varFor);
        else ch1 = this.operande1.chaineCalcul(varFor);
      }
      if (this.operande2.nature() === CCbGlob.natOperation) {
        var op2 = this.operande2.Ope;
        if ((op2 === Ope.Plus) || (op2 === Ope.Moin)
            || (op2 === Ope.Mult) || (op2 === Ope.Divi))
          ch2 = this.operande2.chaineCalculSansPar(varFor);
        else ch2 = this.operande2.chaineCalcul(varFor);
      }
      else ch2 = this.operande2.chaineCalcul(varFor);
      ch = parouv + ch1 + "+" + ch2 + parfer;
      break;
    case Ope.Moin:
      if (this.operande1.nature() === CCbGlob.natOperation) {
        op1 = this.operande1.Ope;
        if ((op1 === Ope.Plus) || (op1 === Ope.Moin)
            || (op1 === Ope.Mult) || (op1 === Ope.Divi))
          ch1 = this.operande1.chaineCalculSansPar(varFor);
        else ch1 = this.operande1.chaineCalcul(varFor);
      }
      else {
        // ch1 = operande1.chaineCalcul(varFor);
        // Rajouté version 4.6
        if (this.operande1.nature() === CCbGlob.natMoinsUnaire) ch1 = this.operande1.chaineCalculSansPar(varFor);
        else ch1 = this.operande1.chaineCalcul(varFor);
      }
      if (this.operande2.nature() === CCbGlob.natOperation) {
        op2 = this.operande2.Ope;
        if ((op2 === Ope.Mult) || (op2 === Ope.Divi))
          ch2 = this.chaineCalculSansPar(varFor);
        else ch2 = this.operande2.chaineCalcul(varFor);
      }
      else ch2 = this.operande2.chaineCalcul(varFor);
      ch = parouv + ch1 + "-" + ch2 + parfer;
      break;
    case Ope.Mult:
      if (this.operande1.nature() === CCbGlob.natOperation) {
        op1 = this.operande1.Ope;
        if ((op1 === Ope.Mult) || (op1 === Ope.Divi))
          ch1 = this.operande1.chaineCalculSansPar(varFor);
        else ch1 = this.operande1.chaineCalcul(varFor);
      }
      else ch1 = this.operande1.chaineCalcul(varFor);
      if (this.operande2.nature() === CCbGlob.natOperation) {
        op2 = this.operande2.Ope;
        if (op2 === Ope.Mult)
          ch2 = this.operande2.chaineCalculSansPar(varFor);
        else ch2 = this.operande2.chaineCalcul(varFor);
      }
      else ch2 = this.operande2.chaineCalcul(varFor);
      ch = parouv + ch1 + "*" + ch2 + parfer;
      break;
    case Ope.Divi:
      if (this.operande1.nature() === CCbGlob.natOperation) {
        op1 = this.operande1.Ope;
        if ((op1 === Ope.Mult) || (op1 === Ope.Divi))
          ch1 = this.operande1.chaineCalculSansPar(varFor);
        else ch1 = this.operande1.chaineCalcul(varFor);
      }
      else ch1 = this.operande1.chaineCalcul(varFor);
      ch2 = this.operande2.chaineCalcul(varFor);
      ch = parouv + ch1 + "/" + ch2 + parfer;
      break;
    case Ope.Inf:
    case Ope.Sup:
    case Ope.InfOuEgal:
    case Ope.SupOuEgal:
    case Ope.Egalite:
    case Ope.Diff:
      if (this.operande1.nature() === CCbGlob.natOperation) {
        // var oper1 = operande1;
        if (Ope.estTest(this.operande1.Ope)) ch1 = this.operande1.chaineCalcul(varFor);
        else ch1 = this.operande1.chaineCalculSansPar(varFor);
      }
      else ch1 = this.operande1.chaineCalculSansPar(varFor);
      if (this.operande2.nature() === CCbGlob.natOperation) {
        // var oper2 = operande2;
        if (Ope.estTest(this.operande2.Ope)) ch2 = this.operande2.chaineCalcul(varFor);
        else ch2 = this.operande2.chaineCalculSansPar(varFor);
      }
      else ch2 = this.operande2.chaineCalculSansPar(varFor);
      ch = parouv + ch1 + Ope.chaineOperateur(this.Ope) + ch2 + parfer;
      break;
    case Ope.And:
    case Ope.Or:
      ch1 = this.operande1.chaineCalculSansPar(varFor);
      var natop2 = this.operande2.nature();
      if (natop2 === CCbGlob.natOperation) {
        op2 = this.operande2.operateur;
        if ((op2 === Ope.And) || (op2 === Ope.Or)) ch2 = this.operande2.chaineCalcul(varFor);
        else ch2 = this.operande2.chaineCalculSansPar(varFor);
      }
      else ch2 = this.operande2.chaineCalculSansPar(varFor);
      ch = parouv + ch1 + Ope.chaineOperateur(this.Ope) + ch2 + parfer;
  } // switch Ope}
  // Au cas où le calcul commençait par un moins unaire, la chaine commence par "0-"
  if (ch.substring(0, 3) === "(0-") ch = parouv + "-" + ch.substring(3);
  return ch;
};
COperation.prototype.chaineCalculSansPar = function(varFor) {
  var ch1 = "", ch2 = "", ch = "";
   switch (this.Ope) {
    case Ope.Plus:
      if (this.operande1.nature() === CCbGlob.natOperation) {
        var op1 = this.operande1.Ope;
        if ((op1 === Ope.Plus) || (op1 === Ope.Moin)
            || (op1 === Ope.Mult) || (op1 === Ope.Divi))
          ch1 = this.operande1.chaineCalculSansPar(varFor);
        else ch1 = this.operande1.chaineCalcul(varFor);
      }
      else {
        if (this.operande1.nature() === CCbGlob.natMoinsUnaire) ch1 = this.operande1.chaineCalculSansPar(varFor);
        else ch1 = this.operande1.chaineCalcul(varFor);
      }
      if (this.operande2.nature() === CCbGlob.natOperation) {
        var op2 = this.operande2.Ope;
        if ((op2 === Ope.Plus) || (op2 === Ope.Moin)
            || (op2 === Ope.Mult) || (op2 === Ope.Divi))
          ch2 = this.operande2.chaineCalculSansPar(varFor);
        else ch2 = this.operande2.chaineCalcul(varFor);
      }
      else ch2 = this.operande2.chaineCalcul(varFor);
      ch = ch1 + "+" + ch2;
      break;
    case Ope.Moin:
      if (this.operande1.nature() === CCbGlob.natOperation) {
        op1 = this.operande1.Ope;
        if ((op1 === Ope.Plus) || (op1 === Ope.Moin)
            || (op1 === Ope.Mult) || (op1 === Ope.Divi))
          ch1 = this.operande1.chaineCalculSansPar(varFor);
        else ch1 = this.operande1.chaineCalcul(varFor);
      }
      else {
        if (this.operande1.nature() === CCbGlob.natMoinsUnaire) ch1 = this.operande1.chaineCalculSansPar(varFor);
        else ch1 = this.operande1.chaineCalcul(varFor);
      }
      if (this.operande2.nature() === CCbGlob.natOperation) {
        op2 = this.operande2.Ope;
        if ((op2 === Ope.Mult) || (op2 === Ope.Divi))
          ch2 = this.operande2.chaineCalculSansPar(varFor);
        else ch2 = this.operande2.chaineCalcul(varFor);
      }
      else ch2 = this.operande2.chaineCalcul(varFor);
      ch = ch1 + "-" + ch2;
      break;
    case Ope.Mult:
      if (this.operande1.nature() === CCbGlob.natOperation) {
        op1 = this.operande1.Ope;
        if ((op1 === Ope.Mult) || (op1 === Ope.Divi))
          ch1 = this.operande1.chaineCalculSansPar(varFor);
        else ch1 = this.operande1.chaineCalcul(varFor);
      }
      else ch1 = this.operande1.chaineCalcul(varFor);
      if (this.operande2.nature() === CCbGlob.natOperation) {
        op2 = this.operande2.Ope;
        if (op2 === Ope.Mult)
          ch2 = this.operande2.chaineCalculSansPar(varFor);
        else ch2 = this.operande2.chaineCalcul(varFor);
      }
      else ch2 = this.operande2.chaineCalcul(varFor);
      ch = ch1 + "*" + ch2;
      break;
    case Ope.Divi:
      if (this.operande1.nature() === CCbGlob.natOperation) {
        op1 = this.operande1.Ope;
        if ((op1 === Ope.Mult) || (op1 === Ope.Divi))
          ch1 = this.operande1.chaineCalculSansPar(varFor);
        else ch1 = this.operande1.chaineCalcul(varFor);
      }
      else ch1 = this.operande1.chaineCalcul(varFor);
      ch2 = this.operande2.chaineCalcul(varFor);
      ch = ch1 + "/" + ch2;
      break;
    // Modifié version 3.0
    case Ope.Inf:
    case Ope.Sup:
    case Ope.InfOuEgal:
    case Ope.SupOuEgal:
    case Ope.Egalite:
    case Ope.Diff:
      if (this.operande1.nature() === CCbGlob.natOperation) {
        // var oper1 = operande1;
        if (Ope.estTest(this.operande1.Ope)) ch1 = this.operande1.chaineCalcul(varFor);
        else ch1 = this.operande1.chaineCalculSansPar(varFor);
      }
      else ch1 = this.operande1.chaineCalculSansPar(varFor);
      if (this.operande2.nature() === CCbGlob.natOperation) {
        // var oper2 = operande2;
        if (Ope.estTest(this.operande2.Ope)) ch2 = this.operande2.chaineCalcul(varFor);
        else ch2 = this.operande2.chaineCalculSansPar(varFor);
      }
      else ch2 = this.operande2.chaineCalculSansPar(varFor);
      ch = ch1 + Ope.chaineOperateur(this.Ope) + ch2;
      break;
    case Ope.And:
    case Ope.Or:
      ch1 = this.operande1.chaineCalculSansPar(varFor);
      var natop2 = this.operande2.nature();
      if (natop2 === CCbGlob.natOperation) {
        op2 = this.operande2.operateur;
        if ((op2 === Ope.And) || (op2 === Ope.Or)) ch2 = this.operande2.chaineCalcul(varFor);
        else ch2 = this.operande2.chaineCalculSansPar(varFor);
      }
      else ch2 = this.operande2.chaineCalculSansPar(varFor);
      ch = ch1 + Ope.chaineOperateur(this.Ope) + ch2;

  } // switch Ope}
  // Pour un calcul complexe nul la chaîne renvoyée est "0+0*i" on renvoie alors "0"
  if (ch === COperation.zeroComp) return "0";
  // Au cas où le calcul commençait par un moins unaire, la chaine commence par "0-"
  if (ch.substring(0, 2) === "0-") ch = ch.substring(1);

  return ch;
};

COperation.prototype.chaineLatex = function(varFor) {
  return "\\left(" + this.chaineLatexSansPar(varFor) + "\\right)";
};

COperation.prototype.chaineLatexSansPar = function(varFor) {
  var ch1 = "", ch2 = "", ch = "";
  switch (this.Ope) {
    case Ope.Plus:
      if (this.operande1.nature() === CCbGlob.natOperation) {
        var op1 = this.operande1.Ope;
        if ((op1 === Ope.Plus) || (op1 === Ope.Moin)
            || (op1 === Ope.Mult) || (op1 === Ope.Divi))
          ch1 = this.operande1.chaineLatexSansPar(varFor);
        else ch1 = this.operande1.chaineLatex(varFor);
      }
      else ch1 = this.operande1.chaineLatexSansPar(varFor);
      if (this.operande2.nature() === CCbGlob.natOperation) {
        var op2 = this.operande2.Ope;
        if ((op2 === Ope.Plus) || (op2 === Ope.Moin)
            || (op2 === Ope.Mult) || (op2 === Ope.Divi))
          ch2 = this.operande2.chaineLatexSansPar(varFor);
        else ch2 = this.operande2.chaineLatex(varFor);
      }
      else ch2 = this.operande2.chaineLatex(varFor);
      if (ch2.charAt(0)==="-") ch = ch1 + "-" + ch2.substring(1);
      else ch = ch1 + "+" + ch2;
      break;
    case Ope.Moin:
      if (this.operande1.nature() === CCbGlob.natOperation) {
        op1 = this.operande1.Ope;
        if ((op1 === Ope.Plus) || (op1 === Ope.Moin)
            || (op1 === Ope.Mult) || (op1 === Ope.Divi))
          ch1 = this.operande1.chaineLatexSansPar(varFor);
        else ch1 = this.operande1.chaineLatex(varFor);
      }
      else {
        if (this.operande1.nature() === CCbGlob.natMoinsUnaire) ch1 = this.operande1.chaineLatexSansPar(varFor);
        else ch1 = this.operande1.chaineLatex(varFor);
      }
      if (this.operande2.nature() === CCbGlob.natOperation) {
        op2 = this.operande2.Ope;
        if ((op2 === Ope.Mult) || (op2 === Ope.Divi))
          ch2 = this.operande2.chaineLatexSansPar(varFor);
        else ch2 = this.operande2.chaineLatex(varFor);
      }
      else ch2 = this.operande2.chaineLatex(varFor);
      ch = ch1 + "-" + ch2;
      break;
    case Ope.Mult:
      var bsignemult = false;
      if (this.operande1.nature() === CCbGlob.natOperation) {
        op1 = this.operande1.Ope;
        if ((op1 === Ope.Mult) || (op1 === Ope.Divi))
          ch1 = this.operande1.chaineLatexSansPar(varFor);
        else ch1 = this.operande1.chaineLatex(varFor);
        // if (op1 == Ope.Divi) bsignemult = true; // Supprimé version 4.9.2
      }
      else ch1 = this.operande1.chaineLatex(varFor);
      if (this.operande2.nature() === CCbGlob.natOperation) {
        op2 = this.operande2.Ope;
        if ((op2 === Ope.Mult) || (op2 === Ope.Divi))
          ch2 = this.operande2.chaineLatexSansPar(varFor);
        else ch2 = this.operande2.chaineLatex(varFor);
        if (op2 === Ope.Divi) bsignemult = true;
      }
      else ch2 = this.operande2.chaineLatex(varFor);
      // ch = ch1 + "*" + ch2; // Sécial pour Latex, pas de signe multiplicatif
      if (bsignemult || Fonte.chiffre(ch2.charAt(0))) ch = ch1 + "\\times " + ch2; // Espace de fin nécessaire
      else ch = ch1 + ch2;
      break;
    case Ope.Divi:
  			var bfrac = true;
  			if (this.operande1.nature() ===  CCbGlob.natOperation) {
  				op1 = this.operande1.Ope;
  				if (op1 === Ope.Mult) {
  					bfrac = false;
  					var operandegauche1 = this.operande1.operande1;
  					if (operandegauche1.nature() === CCbGlob.natOperation) {
  						var opgauche1 = operandegauche1.Ope;
  						if (opgauche1 === Ope.Divi) {
                bfrac = false;
  							ch1 = this.operande1.operande1.chaineLatexSansPar(varFor);
                ch2 = "\\frac{" + this.operande1.operande2.chaineLatexSansPar(varFor) + "} {" + this.operande2.chaineLatexSansPar(varFor) + "}";
              }
  						else {
                bfrac = true;
                ch1 = this.operande1.chaineLatexSansPar(varFor);
                ch2 = this.operande2.chaineLatexSansPar(varFor);
              }
  					}
  					else {
              bfrac = true;
              ch1 = this.operande1.chaineLatexSansPar(varFor);
              ch2 = this.operande2.chaineLatexSansPar(varFor);
            }
  				}
  				else {
  					ch1 = this.operande1.chaineLatexSansPar(varFor);
  					ch2 = this.operande2.chaineLatexSansPar(varFor);
  				}
  			}
  			else {
  				ch1 = this.operande1.chaineLatexSansPar(varFor);
  				ch2 = this.operande2.chaineLatexSansPar(varFor);
  			}
  			if (bfrac) ch = "\\frac{" + ch1 + "}{" + ch2 + "}";
  			else ch = ch1 + "\\times" + ch2;
      break;
    // Modifié version 3.0
    case Ope.Inf:
    case Ope.Sup:
    case Ope.InfOuEgal:
    case Ope.SupOuEgal:
    case Ope.Egalite:
    case Ope.Diff:
      if (this.operande1.nature() === CCbGlob.natOperation) {
        // var oper1 = operande1;
        if (Ope.estTest(this.operande1.Ope)) ch1 = this.operande1.chaineLatex(varFor);
        else ch1 = this.operande1.chaineLatexSansPar(varFor);
      }
      // else ch1 = operande1.chaineCalculSansPar(varFor); Correction défaut version 4.7.2
      else ch1 = this.operande1.chaineLatexSansPar(varFor);
      if (this.operande2.nature() === CCbGlob.natOperation) {
        // var oper2 = operande2;
        if (Ope.estTest(this.operande2.Ope)) ch2 = this.operande2.chaineLatex(varFor);
        else ch2 = this.operande2.chaineLatexSansPar(varFor);
      }
      else ch2 = this.operande2.chaineLatexSansPar(varFor);

      ch = ch1 + Ope.chaineOperateurLatex(this.Ope) + " " + ch2;
      break;
    case Ope.And:
    case Ope.Or:
      ch1 = this.operande1.chaineLatexSansPar(varFor);
      var natop2 = this.operande2.nature();
      if (natop2 === CCbGlob.natOperation) {
        op2 = this.operande2.operateur;
        if ((op2 === Ope.And) || (op2 === Ope.Or)) ch2 = this.operande2.chaineLatex(varFor);
        else ch2 = this.operande2.chaineLatexSansPar(varFor);
      }
      else ch2 = this.operande2.chaineLatexSansPar(varFor);
      ch = ch1 + Ope.chaineOperateurLatex(this.Ope) + ch2;

  } // switch Ope}
  // Pour un calcul complexe nul la chaîne renvoyée est "0+0*i" on renvoie alors "0"
  if (ch === COperation.zeroComp) return "0";
  // Au cas où le calcul commençait par un moins unaire, la chaine commence par "0-"
  if (ch.substring(0, 2) === "0-") ch = ch.substring(1);

  return ch;
};

COperation.prototype.read = function(inps, list) {
  CCb.prototype.read.call(this, inps, list);
  this.Ope = inps.readByte();
  this.operande1 = inps.readObject(list);
  this.operande2 = inps.readObject(list);
};

COperation.prototype.write = function(oups, list) {
  CCb.prototype.write.call(this,oups, list);
  oups.writeByte(this.Ope);
  oups.writeObject(this.operande1);
  oups.writeObject(this.operande2);
};

COperation.prototype.estConstant = function() {
  return this.operande1.estConstant() && this.operande2.estConstant();
};

COperation.prototype.deriveePossible = function(indiceVariable) {
  return this.operande1.deriveePossible(indiceVariable) && this.operande2.deriveePossible(indiceVariable);
};

COperation.prototype.calculAvecValeursRemplacees = function() {
  return new COperation(this.listeProprietaire, this.operande1.calculAvecValeursRemplacees(),
      this.operande2.calculAvecValeursRemplacees(), this.Ope) ;
};

COperation.prototype.membreGauche = function() {
  return this.operande1;
};

COperation.prototype.membreDroit = function() {
  return this.operande2;
};



// WEBPACK FOOTER //
// ./src/objets/COperation.js